import { Component, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import i18n from '../i18n';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error('Error caught by boundary:', error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo?.componentStack } });
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    // Auto-reload after 5s so PWA doesn't permanently hang on white screen.
    if (!prevState.hasError && this.state.hasError) {
      setTimeout(() => window.location.reload(), 5000);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: '#FAF7F2',
          color: '#1c1410',
          padding: '32px 24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          fontFamily: 'Manrope, sans-serif',
          zIndex: 99999,
        }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>⚠️</div>
          <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: '#1c1410' }}>
            {i18n.t('common.somethingWentWrong')}
          </h2>
          <p style={{ fontSize: 13, color: '#6b6258', marginBottom: 18, maxWidth: 320 }}>
            {this.state.error?.message || i18n.t('common.unexpectedError')}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '12px 24px',
              borderRadius: 999,
              border: 'none',
              background: '#FF5A00',
              color: '#fff',
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {i18n.t('common.reloadPage')}
          </button>
          <p style={{ marginTop: 16, fontSize: 11, color: '#9b8e84' }}>
            Auto-reload in 5s…
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}
